/**
 * Logger único do income-front — o único arquivo autorizado a usar `console.*`
 * (`no-console` é `error` no resto do projeto).
 *
 * Em dev, tudo vai para o console. Em produção, só `error`, já sem PII, e
 * `debug` é descartado. O sink (`registerSink`) recebe os eventos para o
 * destino externo (OpenObserve).
 *
 * @example
 *   import logger from '@/services/logger'
 *   logger.error('[useSidePanel] painel não registrado', err)
 */

import { scrub } from '@/services/pii'

const isDev = import.meta.env.DEV

/**
 * @typedef {'debug' | 'info' | 'warn' | 'error'} LogLevel
 * @typedef {(event: { level: LogLevel, args: unknown[], context: object }) => void} LogSink
 */

/** @type {LogSink | null} */
let sink = null

/** @param {LogSink} fn */
export function registerSink(fn) {
  sink = fn
}

/**
 * @param {LogLevel} level
 * @param {unknown[]} args
 * @param {object} [context]
 */
function emit(level, args, context = {}) {
  if (level === 'debug' && !isDev) return

  if (isDev) {
    console[level](...args)
  } else if (level === 'error') {
    console.error(...args.map(arg => scrub(arg)))
  }

  if (sink) {
    try {
      sink({ level, args, context })
    } catch {
      // Um sink com defeito não pode derrubar quem loga.
      if (isDev) console.error('[logger] sink lançou exceção')
    }
  }
}

const logger = {
  /** @param {...unknown} args */
  debug: (...args) => emit('debug', args),
  /** @param {...unknown} args */
  info: (...args) => emit('info', args),
  /** @param {...unknown} args */
  warn: (...args) => emit('warn', args),
  /** @param {...unknown} args */
  error: (...args) => emit('error', args),
  /**
   * @param {unknown} error
   * @param {object} [context]
   */
  capture: (error, context = {}) => emit('error', [error], context),
}

export default logger
